import React, { useState, useEffect } from 'react';

function LaundryService({ onNavigate, userInfo }) {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [notificationsOpen, setNotificationsOpen] = useState(false);
    const [showBookingModal, setShowBookingModal] = useState(false);
    const [bookingData, setBookingData] = useState({
        serviceType: '',
        itemCount: 1,
        pickupSlot: '',
        deliverySlot: '',
        instructions: ''
    });

    // Backend data states
    const [services, setServices] = useState([]);
    const [stats, setStats] = useState([]);
    const [currentBookings, setCurrentBookings] = useState([]);
    const [orderHistory, setOrderHistory] = useState([]);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // API Base URL
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in/api';

    // Fetch all laundry data from backend
    useEffect(() => {
        const fetchLaundryData = async () => {
            try {
                setLoading(true);
                setError(null);

                const userId = userInfo?.userId || userInfo?.id;
                
                if (!userId) {
                    throw new Error('User ID not found');
                }

                const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                const headers = {
                    'Content-Type': 'application/json',
                };

                if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                }

                // Fetch user profile data to get the actual name
                const profileRes = await fetch(`${API_BASE_URL}/student/profile/${userId}`, {
                    method: 'GET',
                    headers
                });
                const profileData = await profileRes.json();
                if (profileData.success) {
                    setUserData(profileData.data);
                }

                // Fetch services
                const servicesRes = await fetch(`${API_BASE_URL}/student/laundry/services`, {
                    method: 'GET',
                    headers
                });
                const servicesData = await servicesRes.json();
                if (servicesData.success) {
                    setServices(servicesData.services);
                }

                // Fetch stats
                const statsRes = await fetch(`${API_BASE_URL}/student/laundry/stats/${userId}`, {
                    method: 'GET',
                    headers
                });
                const statsData = await statsRes.json();
                if (statsData.success) {
                    setStats([
                        statsData.stats.activeOrders,
                        statsData.stats.completed,
                        statsData.stats.totalSpent,
                        statsData.stats.rating
                    ]);
                }

                // Fetch current orders
                const ordersRes = await fetch(`${API_BASE_URL}/student/laundry/orders/${userId}`, {
                    method: 'GET',
                    headers
                });
                const ordersData = await ordersRes.json();
                if (ordersData.success) {
                    setCurrentBookings(ordersData.orders);
                }

                // Fetch order history
                const historyRes = await fetch(`${API_BASE_URL}/student/laundry/history/${userId}`, {
                    method: 'GET',
                    headers
                });
                const historyData = await historyRes.json();
                if (historyData.success) {
                    setOrderHistory(historyData.history);
                }

            } catch (err) {
                console.error('Error fetching laundry data:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchLaundryData();
    }, [userInfo, API_BASE_URL]);

    const timeSlots = [
        '08:00 AM - 10:00 AM',
        '10:00 AM - 12:00 PM',
        '12:00 PM - 02:00 PM',
        '02:00 PM - 04:00 PM',
        '04:00 PM - 06:00 PM',
        '06:00 PM - 08:00 PM'
    ];

    const notifications = [
        {
            id: 1,
            type: 'laundry',
            title: 'Laundry Ready',
            message: 'Your laundry order is ready for pickup',
            time: '1h ago',
            icon: '🧺',
            unread: true
        }
    ];

    const handleBookingSubmit = async () => {
        if (!bookingData.serviceType || !bookingData.pickupSlot || !bookingData.deliverySlot) {
            alert('Please fill in all required fields');
            return;
        }

        try {
            const userId = userInfo?.userId || userInfo?.id;
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');

            const headers = {
                'Content-Type': 'application/json',
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_BASE_URL}/student/laundry/order/${userId}`, {
                method: 'POST',
                headers,
                body: JSON.stringify(bookingData)
            });

            const result = await response.json();

            if (result.success) {
                alert('Laundry service booked successfully!');
                setShowBookingModal(false);
                setBookingData({
                    serviceType: '',
                    itemCount: 1,
                    pickupSlot: '',
                    deliverySlot: '',
                    instructions: ''
                });
                
                // Refresh the data
                window.location.reload();
            } else {
                alert(result.message || 'Failed to book laundry service');
            }
        } catch (error) {
            console.error('Error booking laundry service:', error);
            alert('Error booking laundry service. Please try again.');
        }
    };

    function MenuItem({ icon, label, active = false, route }) {
        return (
            <div
                className={`menu-item ${active ? 'menu-item-active' : ''}`}
                onClick={() => {
                    if (route && onNavigate) {
                        onNavigate(route);
                    }
                    setSidebarOpen(false);
                }}
            >
                <span className="menu-icon">{icon}</span>
                <span className="menu-label">{label}</span>
                <div className="menu-glow"></div>
            </div>
        );
    }

    const handleLogout = () => {
        if (onNavigate) {
            onNavigate('hostel-login');
        }
    };

    // Loading State
    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: '#ffffff',
                flexDirection: 'column',
                gap: '20px'
            }}>
                <div style={{
                    width: '60px',
                    height: '60px',
                    border: '6px solid #f3f3f3',
                    borderTop: '6px solid #2b6777',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                }}></div>
                <p style={{ 
                    color: '#2b6777', 
                    fontSize: '18px', 
                    fontWeight: '600' 
                }}>Loading Laundry Service...</p>
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    // Error State
    if (error) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: '#f4f7fa',
                padding: '2rem'
            }}>
                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '16px',
                    padding: '3rem',
                    textAlign: 'center',
                    maxWidth: '500px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                }}>
                    <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⚠️</div>
                    <h3 style={{ color: '#dc2626', marginBottom: '1rem', fontSize: '1.5rem' }}>
                        Error Loading Laundry Service
                    </h3>
                    <p style={{ color: '#666', marginBottom: '2rem' }}>{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        style={{
                            backgroundColor: '#2b6777',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '0.75rem 1.5rem',
                            cursor: 'pointer',
                            fontSize: '1rem',
                            fontWeight: '600'
                        }}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

                * {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                }

                body {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                    -webkit-font-smoothing: antialiased;
                    overflow-x: hidden;
                }

                @keyframes slideIn {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @keyframes shimmer {
                    0% { background-position: -1000px 0; }
                    100% { background-position: 1000px 0; }
                }

                @keyframes pulse {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                }

                @keyframes glow {
                    0%, 100% { opacity: 0.5; }
                    50% { opacity: 1; }
                }

                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1);
                    }
                }

                .dashboard-container {
                    display: flex;
                    height: 100vh;
                    background: #ffffff;
                    position: relative;
                    overflow: hidden;
                }

                /* Sidebar */
                .sidebar {
                    position: fixed;
                    top: 0;
                    left: 0;
                    bottom: 0;
                    width: 300px;
                    background: linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(242,242,242,0.98) 100%);
                    backdrop-filter: blur(30px);
                    border-right: 2px solid rgba(82,171,152,0.2);
                    transform: translateX(-100%);
                    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                    z-index: 100;
                    display: flex;
                    flex-direction: column;
                    box-shadow: 8px 0 40px rgba(43,103,119,0.15);
                }

                .sidebar.open {
                    transform: translateX(0);
                }

                .sidebar-header {
                    padding: 36px 28px;
                    background: linear-gradient(135deg, rgba(43,103,119,0.05) 0%, rgba(82,171,152,0.05) 100%);
                    border-bottom: 2px solid rgba(82,171,152,0.15);
                    position: relative;
                    overflow: hidden;
                }

                .sidebar-header::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(82,171,152,0.15), transparent);
                    animation: shimmer 4s infinite;
                }

                .sidebar-top {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    margin-bottom: 24px;
                    position: relative;
                    z-index: 1;
                }

                .close-btn {
                    background: rgba(43,103,119,0.08);
                    border: 1.5px solid rgba(82,171,152,0.25);
                    cursor: pointer;
                    font-size: 20px;
                    color: #2b6777;
                    width: 38px;
                    height: 38px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s;
                }

                .close-btn:hover {
                    background: rgba(43,103,119,0.15);
                    border-color: #52ab98;
                    transform: rotate(90deg);
                }

                .user-info-sidebar {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    position: relative;
                    z-index: 1;
                }

                .user-avatar-sidebar {
                    width: 56px;
                    height: 56px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 800;
                    font-size: 22px;
                    color: white;
                    box-shadow: 0 6px 24px rgba(43,103,119,0.3);
                    border: 3px solid rgba(255, 255, 255, 0.5);
                }

                .user-details-sidebar {
                    flex: 1;
                }

                .user-name-sidebar {
                    font-size: 16px;
                    font-weight: 800;
                    color: #2b6777;
                    margin-bottom: 4px;
                }

                .user-role-sidebar {
                    font-size: 13px;
                    color: #52ab98;
                    font-weight: 600;
                }

                .sidebar-nav {
                    flex: 1;
                    padding: 28px 20px;
                    overflow-y: auto;
                }

                .sidebar-nav::-webkit-scrollbar {
                    width: 6px;
                }

                .sidebar-nav::-webkit-scrollbar-track {
                    background: rgba(82,171,152,0.08);
                }

                .sidebar-nav::-webkit-scrollbar-thumb {
                    background: rgba(82,171,152,0.3);
                    border-radius: 3px;
                }

                .menu-item {
                    position: relative;
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    padding: 16px 18px;
                    margin-bottom: 10px;
                    cursor: pointer;
                    color: #2b6777;
                    font-size: 15px;
                    font-weight: 600;
                    border-radius: 14px;
                    transition: all 0.3s;
                    overflow: hidden;
                }

                .menu-glow {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(135deg, rgba(43,103,119,0.08) 0%, rgba(82,171,152,0.08) 100%);
                    opacity: 0;
                    transition: opacity 0.3s;
                }

                .menu-item:hover {
                    color: #52ab98;
                    background: rgba(82,171,152,0.1);
                    transform: translateX(6px);
                    box-shadow: 0 4px 16px rgba(82,171,152,0.15);
                }

                .menu-item:hover .menu-glow {
                    opacity: 1;
                }

                .menu-item-active {
                    background: linear-gradient(135deg, rgba(43,103,119,0.15) 0%, rgba(82,171,152,0.15) 100%);
                    color: #2b6777;
                    box-shadow: 0 6px 20px rgba(82,171,152,0.2);
                    font-weight: 700;
                }

                .menu-item-active .menu-glow {
                    opacity: 1;
                    animation: glow 2s ease-in-out infinite;
                }

                .menu-icon {
                    font-size: 22px;
                    position: relative;
                    z-index: 1;
                }

                .menu-label {
                    position: relative;
                    z-index: 1;
                }

                .logout-section {
                    padding: 20px;
                    border-top: 2px solid rgba(82,171,152,0.15);
                }

                .logout-btn {
                    width: 100%;
                    padding: 16px 20px;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    color: white;
                    border: none;
                    border-radius: 14px;
                    font-size: 15px;
                    font-weight: 700;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    transition: all 0.3s;
                    box-shadow: 0 6px 24px rgba(43,103,119,0.3);
                }

                .logout-btn:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 10px 35px rgba(43,103,119,0.4);
                }

                /* Overlay */
                .overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(43,103,119,0.4);
                    backdrop-filter: blur(6px);
                    z-index: 90;
                    opacity: 0;
                    visibility: hidden;
                    transition: all 0.3s ease;
                }

                .overlay.show {
                    opacity: 1;
                    visibility: visible;
                }

                .booking-modal {
                    position: fixed;
                    inset: 0;
                    background: rgba(43,103,119,0.6);
                    backdrop-filter: blur(10px);
                    z-index: 1000;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                    opacity: 0;
                    visibility: hidden;
                    transition: all 0.3s;
                }

                .booking-modal.show {
                    opacity: 1;
                    visibility: visible;
                }

                /* Main Content */
                .main-content {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    width: 100%;
                    position: relative;
                    z-index: 1;
                }

                /* Top Header */
                .top-header {
                    background: #ffffff;
                    padding: 24px 20px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    border-bottom: 1px solid rgba(82,171,152,0.12);
                    position: relative;
                    box-shadow: 0 2px 8px rgba(43,103,119,0.06);
                }

                .top-header::after {
                    content: '';
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    height: 3px;
                    background: linear-gradient(90deg, #2b6777 0%, #52ab98 100%);
                }

                .header-left {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    flex: 1;
                }

                .menu-toggle {
                    background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
                    border: 2px solid rgba(82,171,152,0.25);
                    cursor: pointer;
                    font-size: 24px;
                    color: #2b6777;
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s;
                    box-shadow: 0 4px 20px rgba(43,103,119,0.15);
                    flex-shrink: 0;
                }

                .menu-toggle:hover {
                    transform: scale(1.05);
                    box-shadow: 0 6px 30px rgba(43,103,119,0.25);
                    background: linear-gradient(135deg, rgba(43,103,119,0.15) 0%, rgba(82,171,152,0.15) 100%);
                }

                .header-title-section {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    min-width: 0;
                    flex: 1;
                }

                .header-greeting {
                    font-size: 13px;
                    color: #52ab98;
                    font-weight: 600;
                    display: none;
                }

                .header-name {
                    font-size: 18px;
                    font-weight: 900;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .header-right {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex-shrink: 0;
                }

                .notification-btn {
                    position: relative;
                    background: rgba(82,171,152,0.1);
                    border: 2px solid rgba(82,171,152,0.2);
                    cursor: pointer;
                    font-size: 20px;
                    color: #2b6777;
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s;
                }

                .notification-btn:hover,
                .notification-btn.active {
                    background: rgba(82,171,152,0.2);
                    border-color: #52ab98;
                    box-shadow: 0 6px 24px rgba(82,171,152,0.3);
                    transform: scale(1.05);
                }

                .notification-badge {
                    position: absolute;
                    top: 4px;
                    right: 4px;
                    min-width: 20px;
                    height: 20px;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    border-radius: 10px;
                    font-size: 10px;
                    font-weight: 800;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0 5px;
                    color: white;
                    border: 2px solid white;
                    box-shadow: 0 3px 12px rgba(43,103,119,0.4);
                    animation: pulse 2s infinite;
                }

                .user-avatar-header {
                    width: 44px;
                    height: 44px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 800;
                    font-size: 18px;
                    color: white;
                    cursor: pointer;
                    border: 3px solid rgba(82,171,152,0.3);
                    transition: all 0.3s;
                    box-shadow: 0 6px 24px rgba(43,103,119,0.3);
                }

                .user-avatar-header:hover {
                    transform: scale(1.1);
                    box-shadow: 0 8px 32px rgba(43,103,119,0.5);
                }

                /* Content Wrapper */
                .content-wrapper {
                    flex: 1;
                    overflow-y: auto;
                    padding: 20px 16px;
                }

                .content-wrapper::-webkit-scrollbar {
                    width: 8px;
                }

                .content-wrapper::-webkit-scrollbar-track {
                    background: rgba(82,171,152,0.08);
                }

                .content-wrapper::-webkit-scrollbar-thumb {
                    background: rgba(82,171,152,0.3);
                    border-radius: 4px;
                }

                .content-area {
                    max-width: 1500px;
                    margin: 0 auto;
                    animation: slideIn 0.6s ease-out;
                }

                /* Hero Section */
                .hero-section {
                    background: linear-gradient(135deg, rgba(43,103,119,0.08) 0%, rgba(82,171,152,0.08) 100%);
                    border-radius: 12px;
                    padding: 28px 20px;
                    margin-bottom: 24px;
                    border: 1px solid rgba(82,171,152,0.15);
                    box-shadow: 0 2px 8px rgba(43,103,119,0.06);
                    position: relative;
                    overflow: hidden;
                }

                .hero-section::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 4px;
                    background: linear-gradient(90deg, #2b6777 0%, #52ab98 100%);
                }

                .hero-content {
                    position: relative;
                    z-index: 1;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 20px;
                }

                .hero-text {
                    flex: 1;
                }

                .hero-title {
                    font-size: 24px;
                    font-weight: 700;
                    color: #2b6777;
                    margin-bottom: 8px;
                    letter-spacing: -0.5px;
                }

                .hero-subtitle {
                    font-size: 13px;
                    color: rgba(43,103,119,0.6);
                    font-weight: 500;
                    line-height: 1.4;
                }

                .book-service-btn {
                    padding: 14px 24px;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    color: white;
                    border: none;
                    border-radius: 12px;
                    font-size: 14px;
                    font-weight: 800;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    transition: all 0.3s;
                    box-shadow: 0 4px 16px rgba(43,103,119,0.2);
                }

                .book-service-btn:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 8px 24px rgba(43,103,119,0.3);
                }

                /* Stats Grid */
                .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 12px;
                    margin-bottom: 24px;
                }

                .stat-card {
                    background: #ffffff;
                    border-radius: 12px;
                    padding: 20px 16px;
                    border: 1px solid rgba(43,103,119,0.12);
                    cursor: pointer;
                    transition: all 0.3s ease;
                    position: relative;
                    overflow: hidden;
                    box-shadow: 0 2px 8px rgba(43,103,119,0.06);
                }

                .stat-card::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 4px;
                    height: 100%;
                    background: linear-gradient(180deg, #2b6777 0%, #52ab98 100%);
                    transform: scaleY(0);
                    transition: transform 0.3s ease;
                }

                .stat-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 8px 24px rgba(43,103,119,0.12);
                    border-color: rgba(82,171,152,0.25);
                }

                .stat-card:hover::before {
                    transform: scaleY(1);
                }

                .stat-header {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    margin-bottom: 16px;
                }

                .stat-label {
                    font-size: 10px;
                    color: rgba(43,103,119,0.6);
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .stat-icon {
                    width: 36px;
                    height: 36px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 18px;
                    background: rgba(82,171,152,0.08);
                    border: 1px solid rgba(82,171,152,0.15);
                }

                .stat-value {
                    font-size: 26px;
                    font-weight: 700;
                    color: #2b6777;
                    margin-bottom: 6px;
                    letter-spacing: -0.5px;
                    line-height: 1;
                }

                .stat-subtext {
                    font-size: 11px;
                    color: rgba(43,103,119,0.5);
                    font-weight: 500;
                }

                /* Services Grid */
                .services-grid {
                    display: grid;
                    grid-template-columns: repeat(1, 1fr);
                    gap: 16px;
                    margin-bottom: 24px;
                }

                .service-card {
                    background: rgba(255,255,255,0.9);
                    backdrop-filter: blur(30px);
                    border-radius: 16px;
                    padding: 24px 20px;
                    border: 2px solid rgba(82,171,152,0.2);
                    cursor: pointer;
                    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                    position: relative;
                    overflow: hidden;
                    box-shadow: 0 4px 16px rgba(43,103,119,0.08);
                }

                .service-card::before {
                    content: '';
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(135deg, rgba(43,103,119,0.08) 0%, rgba(82,171,152,0.08) 100%);
                    opacity: 0;
                    transition: opacity 0.4s;
                }

                .service-card:hover {
                    transform: translateY(-6px);
                    box-shadow: 0 12px 32px rgba(43,103,119,0.18);
                    border-color: rgba(82,171,152,0.4);
                }

                .service-card:hover::before {
                    opacity: 1;
                }

                .service-icon {
                    width: 64px;
                    height: 64px;
                    border-radius: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 32px;
                    margin-bottom: 16px;
                    background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
                    border: 2px solid rgba(82,171,152,0.25);
                    transition: all 0.4s;
                    position: relative;
                    z-index: 1;
                }

                .service-card:hover .service-icon {
                    transform: scale(1.1) rotate(5deg);
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    border-color: transparent;
                }

                .service-info {
                    position: relative;
                    z-index: 1;
                }

                .service-name {
                    font-size: 18px;
                    font-weight: 900;
                    color: #2b6777;
                    margin-bottom: 6px;
                }

                .service-price {
                    font-size: 15px;
                    font-weight: 800;
                    color: #52ab98;
                    margin-bottom: 10px;
                }

                .service-description {
                    font-size: 13px;
                    color: rgba(43,103,119,0.7);
                    margin-bottom: 10px;
                    line-height: 1.5;
                    font-weight: 600;
                }

                .service-duration {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 12px;
                    color: rgba(43,103,119,0.6);
                    font-weight: 700;
                }

                /* Booking Modal */
                .modal-container {
                    background: rgba(255,255,255,0.98);
                    backdrop-filter: blur(40px);
                    border-radius: 20px;
                    padding: 32px 24px;
                    max-width: 700px;
                    width: 100%;
                    max-height: 90vh;
                    overflow-y: auto;
                    box-shadow: 0 20px 50px rgba(43,103,119,0.25);
                    border: 2px solid rgba(82,171,152,0.2);
                    animation: fadeIn 0.3s ease-out;
                }

                .modal-container::-webkit-scrollbar {
                    width: 6px;
                }

                .modal-container::-webkit-scrollbar-track {
                    background: rgba(82,171,152,0.08);
                }

                .modal-container::-webkit-scrollbar-thumb {
                    background: rgba(82,171,152,0.3);
                    border-radius: 3px;
                }

                .modal-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 28px;
                    padding-bottom: 20px;
                    border-bottom: 2px solid rgba(82,171,152,0.15);
                }

                .modal-title {
                    font-size: 22px;
                    font-weight: 900;
                    color: #2b6777;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                .modal-close-btn {
                    background: rgba(43,103,119,0.08);
                    border: 1.5px solid rgba(82,171,152,0.25);
                    cursor: pointer;
                    font-size: 18px;
                    color: #2b6777;
                    width: 36px;
                    height: 36px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s;
                }

                .modal-close-btn:hover {
                    background: rgba(43,103,119,0.15);
                    border-color: #52ab98;
                    transform: rotate(90deg);
                }

                .form-group {
                    margin-bottom: 24px;
                }

                .form-label {
                    display: block;
                    font-size: 13px;
                    font-weight: 800;
                    color: #2b6777;
                    margin-bottom: 10px;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                }

                .form-input,
                .form-select,
                .form-textarea {
                    width: 100%;
                    padding: 14px 16px;
                    border: 2px solid rgba(82,171,152,0.2);
                    border-radius: 10px;
                    font-size: 14px;
                    font-weight: 600;
                    color: #2b6777;
                    background: rgba(82,171,152,0.05);
                    transition: all 0.3s;
                    font-family: 'Inter', sans-serif;
                }

                .form-input:focus,
                .form-select:focus,
                .form-textarea:focus {
                    outline: none;
                    border-color: #52ab98;
                    background: rgba(82,171,152,0.1);
                    box-shadow: 0 4px 16px rgba(82,171,152,0.15);
                }

                .form-textarea {
                    resize: vertical;
                    min-height: 80px;
                }

                .service-options {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 10px;
                }

                .service-option {
                    padding: 14px;
                    border: 2px solid rgba(82,171,152,0.2);
                    border-radius: 10px;
                    background: rgba(82,171,152,0.05);
                    cursor: pointer;
                    transition: all 0.3s;
                    text-align: center;
                }

                .service-option:hover {
                    background: rgba(82,171,152,0.1);
                    border-color: rgba(82,171,152,0.3);
                    transform: translateY(-2px);
                }

                .service-option.selected {
                    background: linear-gradient(135deg, rgba(43,103,119,0.15) 0%, rgba(82,171,152,0.15) 100%);
                    border-color: #52ab98;
                    box-shadow: 0 4px 16px rgba(82,171,152,0.2);
                }

                .service-option-icon {
                    font-size: 28px;
                    margin-bottom: 6px;
                }

                .service-option-name {
                    font-size: 12px;
                    font-weight: 700;
                    color: #2b6777;
                }

                .modal-actions {
                    display: flex;
                    gap: 12px;
                    margin-top: 28px;
                }

                .submit-btn,
                .cancel-btn {
                    flex: 1;
                    padding: 14px 20px;
                    border: none;
                    border-radius: 10px;
                    font-size: 14px;
                    font-weight: 800;
                    cursor: pointer;
                    transition: all 0.3s;
                }

                .submit-btn {
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    color: white;
                    box-shadow: 0 4px 16px rgba(43,103,119,0.2);
                }

                .submit-btn:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 8px 24px rgba(43,103,119,0.3);
                }

                .cancel-btn {
                    background: rgba(82,171,152,0.1);
                    color: #2b6777;
                    border: 2px solid rgba(82,171,152,0.25);
                }

                .cancel-btn:hover {
                    background: rgba(82,171,152,0.2);
                    border-color: #52ab98;
                }

                /* Current Bookings */
                .bookings-list {
                    display: grid;
                    gap: 16px;
                    margin-bottom: 24px;
                }

                .booking-card {
                    background: rgba(255,255,255,0.9);
                    backdrop-filter: blur(30px);
                    border-radius: 16px;
                    padding: 24px 20px;
                    border: 2px solid rgba(82,171,152,0.2);
                    box-shadow: 0 4px 16px rgba(43,103,119,0.08);
                    transition: all 0.3s;
                }

                .booking-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 8px 24px rgba(43,103,119,0.15);
                    border-color: rgba(82,171,152,0.3);
                }

                .booking-header {
                    display: flex;
                    align-items: flex-start;
                    gap: 16px;
                    margin-bottom: 20px;
                    padding-bottom: 16px;
                    border-bottom: 2px solid rgba(82,171,152,0.15);
                }

                .booking-icon-wrapper {
                    width: 52px;
                    height: 52px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 26px;
                    background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
                    border: 2px solid rgba(82,171,152,0.25);
                    flex-shrink: 0;
                }

                .booking-header-info {
                    flex: 1;
                }

                .booking-id-row {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    margin-bottom: 6px;
                    flex-wrap: wrap;
                }

                .booking-id {
                    font-size: 12px;
                    font-weight: 800;
                    color: rgba(43,103,119,0.6);
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .booking-status-badge {
                    padding: 5px 10px;
                    border-radius: 6px;
                    font-size: 10px;
                    font-weight: 800;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                }

                .booking-status-badge.processing {
                    background: rgba(33, 150, 243, 0.15);
                    color: #2196f3;
                    border: 1.5px solid rgba(33, 150, 243, 0.3);
                }

                .booking-status-badge.completed {
                    background: rgba(76, 175, 80, 0.15);
                    color: #4caf50;
                    border: 1.5px solid rgba(76, 175, 80, 0.3);
                }

                .booking-service-name {
                    font-size: 18px;
                    font-weight: 900;
                    color: #2b6777;
                }

                .booking-details {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 12px;
                    padding: 16px;
                    background: rgba(82,171,152,0.05);
                    border-radius: 12px;
                    border: 1.5px solid rgba(82,171,152,0.15);
                    margin-bottom: 16px;
                }

                .detail-item {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }

                .detail-label {
                    font-size: 10px;
                    color: rgba(43,103,119,0.6);
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                }

                .detail-value {
                    font-size: 13px;
                    color: #2b6777;
                    font-weight: 800;
                }

                /* Tracking Steps */
                .tracking-section {
                    background: rgba(82,171,152,0.05);
                    border-radius: 12px;
                    padding: 20px 16px;
                    border: 1.5px solid rgba(82,171,152,0.15);
                }

                .tracking-title {
                    font-size: 14px;
                    font-weight: 800;
                    color: #2b6777;
                    margin-bottom: 16px;
                }

                .tracking-steps {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    position: relative;
                }

                .tracking-steps::before {
                    content: '';
                    position: absolute;
                    top: 16px;
                    left: 20px;
                    right: 20px;
                    height: 2px;
                    background: rgba(82,171,152,0.2);
                    z-index: 0;
                }

                .tracking-step {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 6px;
                    position: relative;
                    z-index: 1;
                    flex: 1;
                }

                .step-circle {
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    background: rgba(255,255,255,0.9);
                    border: 2px solid rgba(82,171,152,0.3);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 14px;
                    transition: all 0.3s;
                }

                .tracking-step.completed .step-circle {
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    border-color: transparent;
                    color: white;
                    box-shadow: 0 4px 12px rgba(43,103,119,0.3);
                }

                .step-label {
                    font-size: 10px;
                    font-weight: 700;
                    color: rgba(43,103,119,0.6);
                    text-align: center;
                    max-width: 70px;
                    line-height: 1.2;
                }

                .tracking-step.completed .step-label {
                    color: #2b6777;
                }

                /* Order History */
                .history-table {
                    background: rgba(255,255,255,0.9);
                    border-radius: 12px;
                    overflow: hidden;
                    border: 1.5px solid rgba(82,171,152,0.15);
                }

                .history-row {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    padding: 16px;
                    border-bottom: 1px solid rgba(82,171,152,0.1);
                    transition: all 0.3s;
                    gap: 10px;
                }

                .history-row:last-child {
                    border-bottom: none;
                }

                .history-row:hover {
                    background: rgba(82,171,152,0.05);
                }

                .history-row.header {
                    background: rgba(82,171,152,0.08);
                    font-weight: 800;
                    color: #2b6777;
                    font-size: 11px;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                    display: none;
                }

                .history-cell {
                    font-size: 13px;
                    color: #2b6777;
                    font-weight: 600;
                }

                .history-cell::before {
                    content: attr(data-label);
                    font-weight: 800;
                    color: rgba(43,103,119,0.6);
                    display: block;
                    font-size: 10px;
                    text-transform: uppercase;
                    margin-bottom: 4px;
                    letter-spacing: 0.3px;
                }

                /* Section */
                .section {
                    background: rgba(255,255,255,0.9);
                    backdrop-filter: blur(30px);
                    border-radius: 16px;
                    padding: 24px 20px;
                    margin-bottom: 24px;
                    border: 2px solid rgba(82,171,152,0.2);
                    box-shadow: 0 4px 16px rgba(43,103,119,0.08);
                }

                .section-header {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin-bottom: 20px;
                    padding-bottom: 16px;
                    border-bottom: 2px solid rgba(82,171,152,0.15);
                }

                .section-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    border: 2px solid rgba(82,171,152,0.3);
                    color: white;
                    box-shadow: 0 4px 16px rgba(43,103,119,0.2);
                    flex-shrink: 0;
                }

                .section-title {
                    font-size: 18px;
                    font-weight: 900;
                    color: #2b6777;
                }

                /* Responsive - Tablet */
                @media (min-width: 768px) {
                    .top-header {
                        padding: 32px 24px;
                    }

                    .header-name {
                        font-size: 22px;
                    }

                    .header-greeting {
                        display: block;
                        font-size: 14px;
                    }

                    .header-title-section {
                        flex-direction: row;
                        align-items: center;
                        gap: 12px;
                    }

                    .content-wrapper {
                        padding: 32px 24px;
                    }

                    .hero-section {
                        padding: 48px 28px;
                    }

                    .hero-title {
                        font-size: 32px;
                    }

                    .hero-subtitle {
                        font-size: 15px;
                    }

                    .book-service-btn {
                        padding: 16px 32px;
                        font-size: 15px;
                        gap: 12px;
                    }

                    .stats-grid {
                        grid-template-columns: repeat(4, 1fr);
                        gap: 16px;
                    }

                    .stat-card {
                        padding: 24px 20px;
                    }

                    .section {
                        padding: 32px 28px;
                    }

                    .section-header {
                        gap: 16px;
                        margin-bottom: 28px;
                    }

                    .section-icon {
                        width: 56px;
                        height: 56px;
                        font-size: 28px;
                    }

                    .section-title {
                        font-size: 22px;
                    }

                    .services-grid {
                        grid-template-columns: repeat(2, 1fr);
                        gap: 20px;
                    }

                    .service-card {
                        padding: 28px 24px;
                    }

                    .service-icon {
                        width: 72px;
                        height: 72px;
                        font-size: 36px;
                        margin-bottom: 16px;
                    }

                    .service-name {
                        font-size: 20px;
                    }

                    .service-price {
                        font-size: 16px;
                    }

                    .bookings-list {
                        gap: 20px;
                    }

                    .booking-card {
                        padding: 28px 24px;
                    }

                    .booking-header {
                        gap: 20px;
                    }

                    .booking-icon-wrapper {
                        width: 56px;
                        height: 56px;
                        font-size: 28px;
                    }

                    .booking-service-name {
                        font-size: 20px;
                    }

                    .booking-details {
                        grid-template-columns: repeat(4, 1fr);
                        gap: 16px;
                        padding: 20px;
                    }

                    .history-row {
                        grid-template-columns: repeat(4, 1fr);
                        gap: 0;
                    }

                    .history-row.header {
                        display: grid;
                    }

                    .history-cell::before {
                        display: none;
                    }

                    .modal-container {
                        padding: 40px;
                    }

                    .modal-title {
                        font-size: 24px;
                    }

                    .service-options {
                        grid-template-columns: repeat(3, 1fr);
                    }
                }

                /* Responsive - Desktop */
                @media (min-width: 1024px) {
                    .sidebar {
                        position: static;
                        transform: translateX(0);
                    }

                    .close-btn {
                        display: none;
                    }

                    .overlay {
                        display: none !important;
                    }

                    .menu-toggle {
                        display: none;
                    }

                    .top-header {
                        padding: 52px 32px;
                    }

                    .header-name {
                        font-size: 28px;
                    }

                    .header-greeting {
                        font-size: 18px;
                    }

                    .content-wrapper {
                        padding: 56px 48px;
                    }

                    .hero-section {
                        padding: 90px 36px;
                    }

                    .hero-title {
                        font-size: 36px;
                    }

                    .hero-subtitle {
                        font-size: 16px;
                    }

                    .book-service-btn {
                        padding: 18px 36px;
                        font-size: 16px;
                    }

                    .stats-grid {
                        gap: 20px;
                    }

                    .stat-card {
                        padding: 28px;
                    }

                    .stat-label {
                        font-size: 12px;
                    }

                    .stat-icon {
                        width: 48px;
                        height: 48px;
                        font-size: 24px;
                    }

                    .stat-value {
                        font-size: 32px;
                    }

                    .stat-subtext {
                        font-size: 13px;
                    }

                    .section {
                        padding: 40px;
                        border-radius: 28px;
                    }

                    .section-header {
                        gap: 20px;
                        margin-bottom: 32px;
                        padding-bottom: 24px;
                    }

                    .section-icon {
                        width: 64px;
                        height: 64px;
                        border-radius: 18px;
                        font-size: 32px;
                    }

                    .section-title {
                        font-size: 28px;
                    }

                    .services-grid {
                        grid-template-columns: repeat(3, 1fr);
                        gap: 24px;
                    }

                    .service-card {
                        padding: 32px;
                        border-radius: 24px;
                    }

                    .service-icon {
                        width: 80px;
                        height: 80px;
                        border-radius: 20px;
                        font-size: 40px;
                        margin-bottom: 20px;
                    }

                    .service-name {
                        font-size: 22px;
                    }

                    .service-price {
                        font-size: 18px;
                    }

                    .service-description {
                        font-size: 14px;
                    }

                    .bookings-list {
                        gap: 24px;
                    }

                    .booking-card {
                        padding: 32px;
                        border-radius: 24px;
                    }

                    .booking-header {
                        gap: 20px;
                        margin-bottom: 24px;
                        padding-bottom: 20px;
                    }

                    .booking-icon-wrapper {
                        width: 64px;
                        height: 64px;
                        border-radius: 16px;
                        font-size: 32px;
                    }

                    .booking-service-name {
                        font-size: 22px;
                    }

                    .booking-details {
                        grid-template-columns: repeat(4, 1fr);
                        gap: 16px;
                        padding: 20px;
                    }

                    .tracking-section {
                        padding: 24px;
                    }

                    .tracking-title {
                        font-size: 16px;
                        margin-bottom: 20px;
                    }

                    .modal-container {
                        padding: 48px;
                    }

                    .modal-title {
                        font-size: 28px;
                    }

                    .service-options {
                        grid-template-columns: repeat(4, 1fr);
                    }
                }

                /* Small mobile adjustments */
                @media (max-width: 380px) {
                    .header-name {
                        font-size: 16px;
                    }

                    .hero-title {
                        font-size: 20px;
                    }

                    .stat-value {
                        font-size: 22px;
                    }

                    .book-service-btn {
                        width: 100%;
                        justify-content: center;
                    }

                    .service-options {
                        grid-template-columns: repeat(2, 1fr);
                    }
                }
            `}</style>

            <div className="dashboard-container">
                {/* Sidebar */}
                <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
                    <div className="sidebar-header">
                        <div className="sidebar-top">
                            <button onClick={() => setSidebarOpen(false)} className="close-btn">
                                ✕
                            </button>
                        </div>
                        <div className="user-info-sidebar">
                            <div className="user-avatar-sidebar">
                                {userData?.fullName ? userData.fullName.charAt(0).toUpperCase() : 
                                 userInfo?.name ? userInfo.name.charAt(0).toUpperCase() : 'S'}
                            </div>
                            <div className="user-details-sidebar">
                                <div className="user-name-sidebar">{userData?.fullName || userInfo?.name || 'Student Name'}</div>
                                <div className="user-role-sidebar">Student Portal</div>
                            </div>
                        </div>
                    </div>

                    <nav className="sidebar-nav">
                        <MenuItem icon="🏠" label="Dashboard" route="hostel-student-dashboard" />
                        <MenuItem icon="🚪" label="Entry/Exit Log" route="hostel-student-entry-exit-log" />
                        <MenuItem icon="🛏" label="Room Details" route="hostel-student-room-details" />
                        <MenuItem icon="🍽️" label="Food Preference" route="hostel-student-food-preference" />
                        <MenuItem icon="🧺" label="Laundry Service" active={true} route="hostel-student-laundry" />
                        <MenuItem icon="💳" label="Grievance" route="hostel-student-payments" />
                        <MenuItem icon="📝" label="Requests" route="hostel-student-requests" />
                        <MenuItem icon="📋" label="Requisition" route="hostel-student-requisition" />
                    </nav>

                    <div className="logout-section">
                        <button onClick={handleLogout} className="logout-btn">
                            🚪 Logout
                        </button>
                    </div>
                </div>

                {/* Overlays */}
                <div
                    className={`overlay ${sidebarOpen ? 'show' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                />

                {/* Booking Modal */}
                <div className={`booking-modal ${showBookingModal ? 'show' : ''}`}>
                    <div className="modal-container">
                        <div className="modal-header">
                            <div className="modal-title">
                                <span>🧺</span> Book Laundry Service
                            </div>
                            <button className="modal-close-btn" onClick={() => setShowBookingModal(false)}>
                                ✕
                            </button>
                        </div>

                        <div className="form-group">
                            <div className="form-label">Select Service</div>
                            <div className="service-options">
                                {services.map((service) => (
                                    <div
                                        key={service.id}
                                        className={`service-option ${bookingData.serviceType === service.id ? 'selected' : ''}`}
                                        onClick={() => setBookingData(prev => ({ ...prev, serviceType: service.id }))}
                                    >
                                        <div className="service-option-icon">{service.icon}</div>
                                        <div className="service-option-name">{service.name}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="form-group">
                            <div className="form-label">Number of Items</div>
                            <input
                                type="number"
                                className="form-input"
                                min="1"
                                value={bookingData.itemCount}
                                onChange={(e) => setBookingData(prev => ({ ...prev, itemCount: parseInt(e.target.value) || 1 }))}
                            />
                        </div>

                        <div className="form-group">
                            <div className="form-label">Pickup Time Slot</div>
                            <select
                                className="form-select"
                                value={bookingData.pickupSlot}
                                onChange={(e) => setBookingData(prev => ({ ...prev, pickupSlot: e.target.value }))}
                            >
                                <option value="">Select pickup slot</option>
                                {timeSlots.map((slot, index) => (
                                    <option key={index} value={slot}>{slot}</option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <div className="form-label">Delivery Time Slot</div>
                            <select
                                className="form-select"
                                value={bookingData.deliverySlot}
                                onChange={(e) => setBookingData(prev => ({ ...prev, deliverySlot: e.target.value }))}
                            >
                                <option value="">Select delivery slot</option>
                                {timeSlots.map((slot, index) => (
                                    <option key={index} value={slot}>{slot}</option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <div className="form-label">Special Instructions (Optional)</div>
                            <textarea
                                className="form-textarea"
                                placeholder="Any special instructions for laundry..."
                                value={bookingData.instructions}
                                onChange={(e) => setBookingData(prev => ({ ...prev, instructions: e.target.value }))}
                            />
                        </div>

                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={() => setShowBookingModal(false)}>
                                Cancel
                            </button>
                            <button className="submit-btn" onClick={handleBookingSubmit}>
                                Book Service
                            </button>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="main-content">
                    {/* Top Header */}
                    <div className="top-header">
                        <div className="header-left">
                            <button
                                className="menu-toggle"
                                onClick={() => setSidebarOpen(true)}
                            >
                                ☰
                            </button>
                            <div className="header-title-section">
                                <div className="header-greeting">Laundry Service •</div>
                                <div className="header-name">{userData?.fullName || userInfo?.name || 'Student'}</div>
                            </div>
                        </div>
                        <div className="header-right">
                            <div className="user-avatar-header">
                                {userData?.fullName ? userData.fullName.charAt(0).toUpperCase() :
                                 userInfo?.name ? userInfo.name.charAt(0).toUpperCase() : 'S'}
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="content-wrapper">
                        <div className="content-area">
                            {/* Hero Section */}
                            <div className="hero-section">
                                <div className="hero-content">
                                    <div className="hero-text">
                                        <h1 className="hero-title">Laundry Service 🧺</h1>
                                        <p className="hero-subtitle">Book professional laundry services for your hostel room</p>
                                    </div>
                                    <button className="book-service-btn" onClick={() => setShowBookingModal(true)}>
                                        <span>➕</span> Book Service
                                    </button>
                                </div>
                            </div>

                            {/* Stats Grid */}
                            <div className="stats-grid">
                                {stats.map((stat, index) => (
                                    <div key={index} className="stat-card">
                                        <div className="stat-header">
                                            <div className="stat-label">{stat.label}</div>
                                            <div className="stat-icon">{stat.icon}</div>
                                        </div>
                                        <div className="stat-value">{stat.value}</div>
                                        <div className="stat-subtext">{stat.subtext}</div>
                                    </div>
                                ))}
                            </div>

                            {/* Services */}
                            <div className="section">
                                <div className="section-header">
                                    <div className="section-icon">✨</div>
                                    <div className="section-title">Available Services</div>
                                </div>
                                <div className="services-grid">
                                    {services.map((service) => (
                                        <div key={service.id} className="service-card">
                                            <div className="service-icon">{service.icon}</div>
                                            <div className="service-info">
                                                <div className="service-name">{service.name}</div>
                                                <div className="service-price">{service.price}</div>
                                                <div className="service-description">{service.description}</div>
                                                <div className="service-duration">
                                                    <span>⏱️</span>
                                                    <span>{service.duration}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Current Bookings */}
                            {currentBookings.length > 0 && (
                                <div className="section">
                                    <div className="section-header">
                                        <div className="section-icon">📦</div>
                                        <div className="section-title">Current Bookings</div>
                                    </div>
                                    <div className="bookings-list">
                                        {currentBookings.map((booking) => (
                                            <div key={booking.id} className="booking-card">
                                                <div className="booking-header">
                                                    <div className="booking-icon-wrapper">
                                                        {booking.icon}
                                                    </div>
                                                    <div className="booking-header-info">
                                                        <div className="booking-id-row">
                                                            <span className="booking-id">{booking.id}</span>
                                                            <span className={`booking-status-badge ${booking.status}`}>
                                                                {booking.status}
                                                            </span>
                                                        </div>
                                                        <div className="booking-service-name">{booking.service}</div>
                                                    </div>
                                                </div>

                                                <div className="booking-details">
                                                    <div className="detail-item">
                                                        <div className="detail-label">Items</div>
                                                        <div className="detail-value">{booking.itemCount} items</div>
                                                    </div>
                                                    <div className="detail-item">
                                                        <div className="detail-label">Amount</div>
                                                        <div className="detail-value">{booking.amount}</div>
                                                    </div>
                                                    <div className="detail-item">
                                                        <div className="detail-label">Pickup Date</div>
                                                        <div className="detail-value">{booking.pickupDate}</div>
                                                    </div>
                                                    <div className="detail-item">
                                                        <div className="detail-label">Delivery Date</div>
                                                        <div className="detail-value">{booking.deliveryDate}</div>
                                                    </div>
                                                </div>

                                                <div className="tracking-section">
                                                    <div className="tracking-title">Order Tracking</div>
                                                    <div className="tracking-steps">
                                                        {booking.trackingSteps.map((step, index) => (
                                                            <div key={index} className={`tracking-step ${step.completed ? 'completed' : ''}`}>
                                                                <div className="step-circle">
                                                                    {step.completed ? '✓' : index + 1}
                                                                </div>
                                                                <div className="step-label">{step.label}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Order History */}
                            {orderHistory.length > 0 && (
                                <div className="section">
                                    <div className="section-header">
                                        <div className="section-icon">📋</div>
                                        <div className="section-title">Order History</div>
                                    </div>
                                    <div className="history-table">
                                        <div className="history-row header">
                                            <div className="history-cell">Date</div>
                                            <div className="history-cell">Service</div>
                                            <div className="history-cell">Items</div>
                                            <div className="history-cell">Amount</div>
                                        </div>
                                        {orderHistory.map((order, index) => (
                                            <div key={index} className="history-row">
                                                <div className="history-cell" data-label="Date">{order.date}</div>
                                                <div className="history-cell" data-label="Service">{order.service}</div>
                                                <div className="history-cell" data-label="Items">{order.items} items</div>
                                                <div className="history-cell" data-label="Amount">{order.amount}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default LaundryService;